/**
 * utilityMethods.ts
 * その他ユーティリティメソッド（コンパス、カメラ、価格推移等）
 * App.vue から切り出し
 */

import store from '@/store'
import { lavelUpdate } from '@/js/pyramid'
import { mapillaryFilterRiset, mapillaryViewer } from '@/js/downLoad'
import { getNextZIndex } from '@/js/downLoad'
import { createShortUrl } from '@/js/permalinkState'

type VM = any

// NOTE: getMapSpeedOverrides / applyMapSpeedProfile は MAP_SPEED_PRESETS（App.vue scope const）に
// 依存するため App.vue に残す

export function onPaintUpdate(vm: VM, paint: Record<string, any> | null) {
  vm.paintSettings = paint || {}
}

export function cancelPointNameEdit(vm: VM) {
  vm.editingPointId = null
  vm.tempPointName = ''
  vm.pointRenameInFlight = false
}

export async function updatePointNameOnServer(vm: VM, pointId: string | number, newName: string) {
  const fd = new FormData()
  fd.append('action', 'job_points.rename')
  fd.append('point_id', String(pointId))
  fd.append('point_name', newName)
  const res = await fetch('/oh3/php/user_kansoku.php', { method: 'POST', body: fd })
  const json = await res.json().catch(() => ({}))
  if (!res.ok || !json?.ok) throw new Error(json?.error || `HTTP ${res.status}`)
  return json.data || json
}

export function _stopIntervalAndFinalize(vm: VM) {
  if (vm.kansokuTimer) {
    clearInterval(vm.kansokuTimer)
    vm.kansokuTimer = null
  }
  vm.kansokuStop?.()
}

export async function _tickKansoku(vm: VM) {
  if (vm.kansokuPhase !== 'observing') return
  if (vm.kansokuInFlight) return

  const nGoal = Number(vm.kansokuCount || 0)
  const nNow = Number(vm.pendingObservation?.n || 0)

  if (nGoal > 0 && nNow >= nGoal) {
    _stopIntervalAndFinalize(vm)
    return
  }

  vm.kansokuInFlight = true
  try {
    await vm.kansokuCollectOnce()
    const nAfter = Number(vm.pendingObservation?.n || 0)
    if (nGoal > 0 && nAfter >= nGoal) {
      _stopIntervalAndFinalize(vm)
    }
  } catch (e) {
    console.warn('[kansoku] collect failed; continue:', e)
  } finally {
    vm.kansokuInFlight = false
  }
}

export function compassClick(vm: VM) {
  const map01 = vm.$store.state.map01
  const map02 = vm.$store.state.map02
  const wasSyncMode = vm.$store.state.mapSyncMode
  vm.$store.commit('setMapSyncMode', false)

  if (map01) {
    map01.jumpTo({ pitch: 0, bearing: 0 })
  }
  if (map02) {
    map02.jumpTo({ pitch: 0, bearing: 0 })
  }

  vm.$nextTick(() => {
    vm.$store.commit('setMapSyncMode', wasSyncMode)
  })
}

export function mapillaryFilterOpen(vm: VM) {
  vm.$store.dispatch('showFloatingWindow', 'mapillary-filter')
}

export function openZoomInfo(vm: VM) {
  vm.$store.dispatch('showFloatingWindow', 'zoom-info')
}

export function mapillaryClose() {
  mapillaryFilterRiset()
}

export function addressSearchClose(vm: VM) {
  vm.$store.dispatch('hideFloatingWindow', 'address-search')
}

export function clearAllAddressMarkers(vm: VM) {
  vm.$refs.addressSearch?.clearAllMarkers()
}

export function zoomInfoClose() {
  // 必要に応じてクリーンアップ処理を追加
}

export function openPriceHistoryWindow(vm: VM, data: { lat: number; lng: number; type: string; location?: string; lotNumber?: string }) {
  const { lat, lng, type, location, lotNumber } = data
  const id = Date.now() + '-' + Math.random().toString(36).substr(2, 9)

  let top = vm.priceHistoryDefaultPos.top
  let left = vm.priceHistoryDefaultPos.left

  if (vm.priceHistoryWindows.length > 0) {
    const lastWin = vm.priceHistoryWindows[vm.priceHistoryWindows.length - 1]
    const isAtDefault = lastWin.top === vm.priceHistoryDefaultPos.top &&
                        lastWin.left === vm.priceHistoryDefaultPos.left
    if (isAtDefault || !lastWin.moved) {
      top = lastWin.top + vm.priceHistoryOffset
      left = lastWin.left + vm.priceHistoryOffset
    }
  }

  const newWindow = {
    id, lat, lng, type,
    location: location || '',
    lotNumber: lotNumber || '',
    top, left, moved: false
  }

  vm.priceHistoryWindows.push(newWindow)

  vm.$nextTick(() => {
    vm.$store.commit('setFloatingVisible', {
      id: 'price-history-' + id,
      visible: true
    })
  })
}

export function closePriceHistoryWindow(vm: VM, id: string) {
  vm.priceHistoryWindows = vm.priceHistoryWindows.filter((w: any) => w.id !== id)
}

export function onPriceHistoryPositionChange(vm: VM, id: string, pos: { top: number; left: number }) {
  const win = vm.priceHistoryWindows.find((w: any) => w.id === id)
  if (win) {
    win.top = pos.top
    win.left = pos.left
    win.moved = true
  }
}

export function downloadPriceHistoryCsv(vm: VM, id: string) {
  const refName = `priceChart-${id}`
  const ref = vm.$refs[refName]
  const component = Array.isArray(ref) ? ref[0] : ref
  if (component?.downloadCsv) {
    component.downloadCsv()
  }
}

export function onTimeSliderClose() {
  // タイムスライダーのクリーンアップは TimeSlider.vue 内で処理される
}

export function openTimeSliderIfKonjyakuExists(vm: VM) {
  const layers = vm.s_selectedLayers?.map01 || []
  const hasKonjyaku = layers.some((layer: any) => layer.id && layer.id.startsWith('oh-kon-'))
  if (hasKonjyaku) {
    vm.$store.commit('setFloatingVisible', {
      id: 'time-slider-window',
      visible: true
    })
  }
}

export function popupDialogClose(vm: VM) {
  const id = store.state.drawFeatureId
  lavelUpdate(null, id)
  vm.s_popupDialog = false
}

export function isDisabledChange(vm: VM) {
  vm.isDisabled2 = false
}

export function triggerCamera() {
  const el = document.getElementById('iosCamInput')
  if (el) el.click()
}

export function onCameraPicked(vm: VM, e: Event) {
  const target = e.target as HTMLInputElement | null
  const file = target?.files?.[0] || null
  if (file) {
    vm.s_selectedFile = file
    vm.onFileSelected()
  }
  if (target) target.value = ''
}

export async function test(vm: VM) {
  vm.$store.dispatch('showFloatingWindow', 'job-picker')
  vm.isJobMenu = true
}

export function openPaintEditorWindow(vm: VM) {
  vm.$store.dispatch('showFloatingWindow', 'painteditor')
}

export function onWidthChangedForMapillary() {
  try {
    (mapillaryViewer as any)?.resize()
  } catch (e) {}
}

export function onWidthChanged(vm: VM, newWidth: number) {
  vm.qrCodeWidth = newWidth
}

export function openWindow(vm: VM) {
  const willShow = !vm.$store.state.floatingWindows['qrcode']
  vm.$store.dispatch('toggleFloatingWindow', 'qrcode')

  if (willShow) {
    // 開く時: 短縮URL生成 + map移動リスナー登録（Dialog-shareと同じ挙動）
    vm.$store.state.updatePermalinkFire = !vm.$store.state.updatePermalinkFire
    setTimeout(() => createShortUrl(vm), 300)
    const map = vm.$store.state.map01
    if (map) {
      const handler = () => {
        vm.$store.state.updatePermalinkFire = !vm.$store.state.updatePermalinkFire
        setTimeout(() => createShortUrl(vm), 300)
      }
      map.on('moveend', handler)
      vm._qrCodeMoveHandler = handler
    }
  } else {
    // 閉じる時: map移動リスナー解除
    const map = vm.$store.state.map01
    if (map && vm._qrCodeMoveHandler) {
      map.off('moveend', vm._qrCodeMoveHandler)
      vm._qrCodeMoveHandler = null
    }
  }
}

export function setMaxZIndex(vm: VM, targetEl: HTMLElement) {
  targetEl.style.zIndex = getNextZIndex()
}

export function startScaleUp(vm: VM) {
  vm.scaleUpStep = 1
  vm.scaleUpInterval = setInterval(() => {
    vm.scaleUpStep += 1
    vm.scaleValue = vm.scaleValue + vm.scaleUpStep
    vm.onScaleInput(vm.scaleValue)
  }, 100)
}

export function stopScaleUp(vm: VM) {
  if (vm.scaleUpInterval) {
    clearInterval(vm.scaleUpInterval)
    vm.scaleUpInterval = null
  }
  vm.scaleUpStep = 1
}
